import { useState, useEffect, useCallback, useMemo } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { authHeaders, Toast } from './listPageStyles';
import { StatusBadge } from './ui';
import { usePageMeta } from '../hooks/usePageMeta';
import {
  DetailShell, Section, Grid, Icon, SubModal, SplitButton,
  FieldText, FieldSelect, FieldDate, FieldCustom,
} from './detail/DetailShell';
import { usePageActions } from '../contexts/PermissionContext';

const API_BASE = '/api/Tenants';
const LICENSES_API = '/api/Licenses';
const LANGUAGES_API = '/api/Languages';

// Kullanıcılar sekmesi — backend GetTenantUsersQueryHandler.Classify ile birebir tür kümesi.
// Sıra, özet rozetlerinin ve filtre düğmelerinin görünüm sırasıdır.
const USER_TYPES = [
  { key: 'MOBILE', label: 'Mobil', tone: 'ok' },
  { key: 'BACKOFFICE', label: 'Back Office', tone: 'neutral' },
  { key: 'DISTRIBUTOR', label: 'Distribütör', tone: 'wait' },
  { key: 'ADMIN', label: 'Admin', tone: 'bad' },
];
const USER_TYPE_BY_KEY = Object.fromEntries(USER_TYPES.map((t) => [t.key, t]));

// Şifre zorluk seviyeleri — backend PasswordPolicy ile birebir eşleşir.
const PASSWORD_LEVELS = [
  { value: '1', label: 'Seviye 1 — Çok Düşük (en az 4 karakter)', min: 4 },
  { value: '2', label: 'Seviye 2 — Düşük (en az 6 karakter)', min: 6 },
  { value: '3', label: 'Seviye 3 — Orta (en az 8, harf + rakam)', min: 8 },
  { value: '4', label: 'Seviye 4 — Yüksek (en az 10, büyük/küçük harf + rakam)', min: 10 },
  { value: '5', label: 'Seviye 5 — Çok Yüksek (en az 12, büyük/küçük + rakam + özel)', min: 12 },
];
const SSL_OPTIONS = [
  { value: 'true', label: 'Evet (SSL/TLS)' },
  { value: 'false', label: 'Hayır' },
];

const EMPTY_FORM = {
  id: '', identifier: '', name: '', languageId: '',
  licenseId: '', licenseStartDate: '',
  adminEmail: '', adminPassword: '', adminFirstName: '', adminLastName: '',
  passwordPolicyLevel: '2',
  smtpHost: '', smtpPort: '', smtpEnableSsl: 'true',
  smtpUsername: '', smtpPassword: '', smtpFromAddress: '', smtpFromName: '',
};

function pick(obj, ...keys) {
  if (!obj) return undefined;
  for (const k of keys) if (obj[k] !== undefined && obj[k] !== null) return obj[k];
  return undefined;
}
function unwrap(payload) { return pick(payload, 'data', 'Data') ?? payload; }
function parseLicence(licence) {
  if (!licence) return null;
  try { return JSON.parse(licence); } catch { return null; }
}
function rowToForm(row) {
  return {
    id: pick(row, 'id', 'Id') ?? '',
    identifier: pick(row, 'identifier', 'Identifier') ?? '',
    name: pick(row, 'name', 'Name') ?? '',
    // Select değeri string karşılaştırır; sayısal LanguageId string'e çevrilmezse dropdown
    // kayıtlı dili seçili göstermez (boş görünür ve kaydedince dil sıfırlanırdı).
    languageId: (pick(row, 'languageId', 'LanguageId') ?? '').toString(),
    licenseId: (pick(row, 'licenseId', 'LicenseId') ?? '').toString(),
    licenseStartDate: (pick(row, 'licenseStartDate', 'LicenseStartDate') ?? '').toString().slice(0, 10),
    adminEmail: '', adminPassword: '', adminFirstName: '', adminLastName: '',
    passwordPolicyLevel: (pick(row, 'passwordPolicyLevel', 'PasswordPolicyLevel') ?? 2).toString(),
    smtpHost: pick(row, 'smtpHost', 'SmtpHost') ?? '',
    smtpPort: (pick(row, 'smtpPort', 'SmtpPort') ?? '').toString(),
    smtpEnableSsl: ((pick(row, 'smtpEnableSsl', 'SmtpEnableSsl') ?? true) ? 'true' : 'false'),
    smtpUsername: pick(row, 'smtpUsername', 'SmtpUsername') ?? '',
    smtpPassword: pick(row, 'smtpPassword', 'SmtpPassword') ?? '',
    smtpFromAddress: pick(row, 'smtpFromAddress', 'SmtpFromAddress') ?? '',
    smtpFromName: pick(row, 'smtpFromName', 'SmtpFromName') ?? '',
  };
}
function fmtDate(v) { if (!v) return '-'; try { return new Date(v).toLocaleDateString('tr-TR'); } catch { return '-'; } }

export function TenantDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const isNew = !id || id === 'new';
  const { canAdd, canEdit, canDelete } = usePageActions();
  const canSave = isNew ? canAdd : canEdit;
  usePageMeta(isNew ? 'Yeni Tenant' : 'Tenant Detayı', 'Tenant tanımı, lisans ataması ve ilk yönetici bilgileri.');

  const [form, setForm] = useState(EMPTY_FORM);
  const [licenses, setLicenses] = useState([]);
  const [languages, setLanguages] = useState([]);
  const [currentSnapshot, setCurrentSnapshot] = useState(null); // mevcut atanmış lisans snapshot'ı (edit)
  const [loading, setLoading] = useState(!isNew);
  const [saving, setSaving] = useState(false);
  const [tried, setTried] = useState(false);
  const [toast, setToast] = useState(null);
  const [deleteConfirm, setDeleteConfirm] = useState(false);
  const [audit, setAudit] = useState(null);

  // Kullanıcılar sekmesi (salt-görüntüleme)
  const [tab, setTab] = useState('info');
  const [users, setUsers] = useState([]);
  const [usersLoading, setUsersLoading] = useState(false);
  const [usersLoaded, setUsersLoaded] = useState(false);
  const [typeFilter, setTypeFilter] = useState('ALL');

  const showToast = (type, message) => { setToast({ type, message }); setTimeout(() => setToast(null), 3500); };
  const set = (k, v) => setForm((c) => ({ ...c, [k]: v }));

  // Lisans planları (atama için)
  useEffect(() => {
    fetch(`${LICENSES_API}/Select`, { headers: authHeaders() })
      .then((r) => (r.ok ? r.json() : null))
      .then((body) => {
        const arr = unwrap(body);
        setLicenses(Array.isArray(arr) ? arr : []);
      })
      .catch(() => setLicenses([]));
  }, []);

  // Dil tanımları — tenant'ın arayüz/bildirim dili buradan seçilir. Login yanıtındaki dil kodunu
  // AuthManager.LookupLanguageCode bu tablodan çözer; seçilmezse "tr-TR" varsayılanına düşer.
  useEffect(() => {
    fetch(`${LANGUAGES_API}/Select`, { headers: authHeaders() })
      .then((r) => (r.ok ? r.json() : null))
      .then((body) => {
        const arr = unwrap(body);
        setLanguages(Array.isArray(arr) ? arr : []);
      })
      .catch(() => setLanguages([]));
  }, []);

  const applyRow = useCallback((row) => {
    setForm(rowToForm(row));
    setCurrentSnapshot(parseLicence(pick(row, 'licence', 'Licence')));
    setAudit({ createdBy: pick(row, 'createdBy', 'CreatedBy', 'createdUser', 'CreatedUser'), createdDate: pick(row, 'createdDate', 'CreatedDate'), updatedBy: pick(row, 'updatedBy', 'UpdatedBy', 'modifiedUser', 'ModifiedUser'), updatedDate: pick(row, 'updatedDate', 'UpdatedDate', 'modifiedDate', 'ModifiedDate') });
  }, []);

  const load = useCallback(async () => {
    if (isNew) { setLoading(false); return; }
    const stateRow = location.state?.row;
    if (stateRow) {
      applyRow(stateRow);
      // Nav-state satırı liste OData $select'inden gelir; audit alanlarını (createdDate/By…)
      // İÇERMEZ → info butonu gizli kalır. Tam kaydı arka planda çekip audit'i ondan doldur
      // (paint'i bloklamaz).
      const decoded = decodeURIComponent(id);
      fetch(`${API_BASE}?$filter=Id eq '${decoded.replace(/'/g, "''")}'&$top=1`, { headers: authHeaders() })
        .then((res) => (res.ok ? res.json() : null))
        .then((data) => {
          const arr = Array.isArray(data) ? data : (data?.value ?? data?.data ?? []);
          const rec = arr && arr[0];
          if (rec) setAudit({ createdBy: pick(rec, 'createdBy', 'CreatedBy', 'createdUser', 'CreatedUser'), createdDate: pick(rec, 'createdDate', 'CreatedDate'), updatedBy: pick(rec, 'updatedBy', 'UpdatedBy', 'modifiedUser', 'ModifiedUser'), updatedDate: pick(rec, 'updatedDate', 'UpdatedDate', 'modifiedDate', 'ModifiedDate') });
        })
        .catch(() => {});
      setLoading(false);
      return;
    }
    setLoading(true);
    try {
      const decoded = decodeURIComponent(id);
      const res = await fetch(`${API_BASE}?$filter=Id eq '${decoded.replace(/'/g, "''")}'&$top=1`, { headers: authHeaders() });
      const data = res.ok ? await res.json() : null;
      const arr = Array.isArray(data) ? data : (data?.value ?? data?.data ?? []);
      if (arr && arr[0]) applyRow(arr[0]);
      else showToast('error', 'Tenant bulunamadı');
    } catch {
      showToast('error', 'Tenant yüklenemedi');
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, isNew, applyRow]);

  useEffect(() => { load(); }, [load]);

  // Kullanıcılar sekmesi açıldığında bu tenant'ın kullanıcılarını çek (tür sınıflandırmalı, salt-görüntüleme).
  // Tenant.Id == User.TenantId ile eşleşir; SuperAdmin yetkisiyle backend elle (fail-closed) filtreler.
  const loadUsers = useCallback(async (force = false) => {
    if (isNew || !form.id) return;
    if (usersLoaded && !force) return;
    setUsersLoading(true);
    try {
      const res = await fetch(`${API_BASE}/Users?tenantId=${encodeURIComponent(form.id)}`, { headers: authHeaders() });
      const body = await res.json().catch(() => null);
      if (!res.ok || pick(body, 'success', 'Success') === false) {
        throw new Error(pick(body, 'message', 'Message') || 'Kullanıcılar alınamadı');
      }
      const list = unwrap(body);
      setUsers(Array.isArray(list) ? list : []);
      setUsersLoaded(true);
    } catch (e) {
      showToast('error', e.message || 'Kullanıcılar yüklenemedi');
      setUsers([]);
    } finally {
      setUsersLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isNew, form.id, usersLoaded]);

  useEffect(() => {
    if (tab === 'users') loadUsers();
  }, [tab, loadUsers]);

  // Tür bazında aktif/toplam kullanıcı sayıları: rozet özeti AKTİF, filtre düğmeleri TOPLAM gösterir.
  const typeCounts = useMemo(() => {
    const counts = { ALL: { active: 0, total: 0 } };
    USER_TYPES.forEach((t) => { counts[t.key] = { active: 0, total: 0 }; });
    users.forEach((u) => {
      const k = pick(u, 'userType', 'UserType') || 'BACKOFFICE';
      const active = !!pick(u, 'isActive', 'IsActive');
      if (!counts[k]) counts[k] = { active: 0, total: 0 };
      counts[k].total += 1;
      counts.ALL.total += 1;
      if (active) { counts[k].active += 1; counts.ALL.active += 1; }
    });
    return counts;
  }, [users]);

  const filteredUsers = useMemo(() => {
    if (typeFilter === 'ALL') return users;
    return users.filter((u) => (pick(u, 'userType', 'UserType') || 'BACKOFFICE') === typeFilter);
  }, [users, typeFilter]);

  const licenseOptions = useMemo(() => {
    const opts = [{ value: '', label: '— Lisans atanmadı —' }];
    licenses.forEach((l) => {
      const lid = pick(l, 'id', 'Id');
      const name = pick(l, 'name', 'Name');
      const ed = pick(l, 'edition', 'Edition');
      const mu = pick(l, 'maxUsers', 'MaxUsers');
      opts.push({ value: String(lid), label: `${name} — ${ed}, ${mu} kullanıcı` });
    });
    return opts;
  }, [licenses]);

  // Yalnız aktif diller seçilebilir; pasif edilmiş bir dil listede kalsaydı seçilebilir ama
  // AuthManager (IsActive filtresi) onu çözemez, tenant sessizce tr-TR'ye düşerdi.
  const languageOptions = useMemo(() => {
    const opts = [{ value: '', label: '— Varsayılan (Türkçe) —' }];
    languages
      .filter((l) => pick(l, 'isActive', 'IsActive') !== false)
      .forEach((l) => {
        const lid = pick(l, 'id', 'Id');
        const name = pick(l, 'name', 'Name');
        const code = pick(l, 'code', 'Code');
        opts.push({ value: String(lid), label: code ? `${name} (${code})` : String(name) });
      });
    return opts;
  }, [languages]);

  const selectedLicense = useMemo(
    () => licenses.find((l) => String(pick(l, 'id', 'Id')) === String(form.licenseId)),
    [licenses, form.licenseId]
  );

  const previewEnd = useMemo(() => {
    if (!selectedLicense) return null;
    const dur = pick(selectedLicense, 'durationDays', 'DurationDays');
    if (!dur) return null;
    const start = form.licenseStartDate ? new Date(form.licenseStartDate) : new Date();
    const end = new Date(start); end.setDate(end.getDate() + Number(dur));
    return end;
  }, [selectedLicense, form.licenseStartDate]);

  const persist = async ({ andNew = false } = {}) => {
    setTried(true);
    if (!form.identifier.trim() || !form.name.trim()) { showToast('error', 'Tanımlayıcı ve Tenant adı zorunludur.'); return; }
    if (isNew) {
      if (!form.adminEmail.trim()) { showToast('error', 'Admin e-posta zorunludur.'); return; }
      // Admin şifresi girilmez: sistem tenant politikasına uygun geçici şifre üretir + mail'ler,
      // admin ilk girişte değiştirir (MustChangePassword).
    }
    setSaving(true);
    try {
      const payload = {
        id: form.id || undefined,
        identifier: form.identifier.trim(),
        name: form.name.trim(),
        languageId: form.languageId === '' ? null : Number(form.languageId),
        licenseId: form.licenseId === '' ? null : Number(form.licenseId),
        licenseStartDate: form.licenseId && form.licenseStartDate ? new Date(form.licenseStartDate).toISOString() : null,
        passwordPolicyLevel: Number(form.passwordPolicyLevel) || 2,
        smtpHost: form.smtpHost.trim() || null,
        smtpPort: form.smtpPort === '' ? null : Number(form.smtpPort),
        smtpEnableSsl: form.smtpEnableSsl === '' ? null : form.smtpEnableSsl === 'true',
        smtpUsername: form.smtpUsername.trim() || null,
        smtpPassword: form.smtpPassword || null,
        smtpFromAddress: form.smtpFromAddress.trim() || null,
        smtpFromName: form.smtpFromName.trim() || null,
      };
      let res;
      if (isNew) {
        res = await fetch(`${API_BASE}/Add`, {
          method: 'POST', headers: authHeaders(),
          body: JSON.stringify({
            ...payload,
            adminEmail: form.adminEmail.trim(),
            // adminPassword gönderilmez → backend geçici şifre üretir + mail'ler.
            adminFirstName: form.adminFirstName.trim(),
            adminLastName: form.adminLastName.trim(),
          }),
        });
      } else {
        res = await fetch(`${API_BASE}/Update`, { method: 'PUT', headers: authHeaders(), body: JSON.stringify(payload) });
      }
      const body = await res.json().catch(() => null);
      if (!res.ok || pick(body, 'success', 'Success') === false) {
        throw new Error(pick(body, 'message', 'Message', 'error') || `Kayıt başarısız (${res.status})`);
      }
      if (isNew && andNew) {
        showToast('success', 'Tenant oluşturuldu. Yeni kayda devam edebilirsiniz.');
        setForm(EMPTY_FORM); setTried(false);
        window.scrollTo({ top: 0, behavior: 'smooth' });
        return;
      }
      showToast('success', isNew ? 'Tenant oluşturuldu.' : 'Tenant güncellendi.');
      setTimeout(() => navigate('/tenants'), 600);
    } catch (e) {
      showToast('error', e.message || 'Kayıt sırasında hata oluştu');
    } finally {
      setSaving(false);
    }
  };

  const handleSave = () => persist();

  const handleDelete = async () => {
    setSaving(true);
    try {
      const res = await fetch(`${API_BASE}/Delete`, { method: 'PUT', headers: authHeaders(), body: JSON.stringify({ id: form.id }) });
      const body = await res.json().catch(() => null);
      if (!res.ok || pick(body, 'success', 'Success') === false) throw new Error(pick(body, 'message', 'Message') || `Silme başarısız (${res.status})`);
      showToast('success', 'Tenant silindi.');
      setTimeout(() => navigate('/tenants'), 400);
    } catch (e) {
      showToast('error', e.message || 'Silme başarısız');
      setSaving(false);
      setDeleteConfirm(false);
    }
  };

  const infoActions = (
    <>
      {!isNew && canDelete && <button className="dps-btn danger" onClick={() => setDeleteConfirm(true)} disabled={saving}><Icon name="x" size={16} /> Sil</button>}
      {!isNew && <button className="dps-btn ghost" onClick={() => navigate('/tenant-usage')} disabled={saving}>Kullanım</button>}
      <button className="dps-btn ghost" onClick={() => navigate('/tenants')} disabled={saving}>İptal</button>
      {canSave && (isNew ? (
        <SplitButton
          label={saving ? 'Kaydediliyor…' : 'Oluştur'} icon="check" onClick={handleSave} disabled={saving}
          menu={[{ label: 'Kaydet ve Yeni', desc: 'Kaydet, listeye dönme, yeni kayıt aç', icon: 'plus', onClick: () => persist({ andNew: true }) }]}
        />
      ) : (
        <button className="dps-btn primary" onClick={handleSave} disabled={saving}>
          <Icon name="check" size={17} /> {saving ? 'Kaydediliyor…' : 'Kaydet'}
        </button>
      ))}
    </>
  );

  const usersActions = (
    <>
      <button className="dps-btn ghost" onClick={() => navigate('/tenants')}>Listeye Dön</button>
      <button className="dps-btn ghost" onClick={() => loadUsers(true)} disabled={usersLoading}>
        {usersLoading ? 'Yükleniyor…' : 'Yenile'}
      </button>
    </>
  );

  return (
    <DetailShell
      crumb={[{ label: "Tenant'lar", to: '/tenants' }, { label: isNew ? 'Yeni Tenant' : 'Tenant Detayı', current: true }]}
      tabs={[
        { key: 'info', label: 'Tenant Bilgileri', icon: 'briefcase' },
        { key: 'users', label: 'Kullanıcılar', icon: 'users', disabled: isNew, title: isNew ? 'Önce tenant kaydedin' : undefined },
      ]}
      activeTab={tab}
      onTab={setTab}
      actions={tab === 'users' ? usersActions : infoActions}
      audit={isNew ? null : audit}
      history={isNew ? null : { entityName: 'Tenant', entityId: id }}
      loading={loading}
    >
      {tab === 'info' && (
      <div className="dps-form">
        <Section icon="briefcase" title="Tenant Bilgileri" subtitle="Tanımlayıcı, ad, dil ve kimlik">
          <Grid cols={2}>
            <FieldText form={form} set={set} tried={tried} k="identifier" label="Tanımlayıcı (Identifier)" req mono readOnly={!isNew} placeholder="orn: fronta" />
            <FieldText form={form} set={set} tried={tried} k="name" label="Tenant Adı" req placeholder="Firma adı" />
            <FieldSelect form={form} set={set} k="languageId" label="Dil" options={languageOptions}
              hint="Bu tenant'ın kullanıcılarına dönen arayüz dili. Seçilmezse Türkçe (tr-TR) kullanılır." />
            {/* #709: Tenant ID benzersiz kimliği sistem tarafından otomatik atanır → alan her zaman
                disabled. Elle girişe kapalı; boş bırakıldığında backend GUID üretir. */}
            <FieldText form={form} set={set} k="id" label="Tenant ID" mono readOnly
              placeholder={isNew ? 'Sistem tarafından otomatik üretilir' : ''}
              hint={isNew ? 'Benzersiz kimlik sistem tarafından atanır; elle girilmez.' : undefined} />
          </Grid>
        </Section>

        <Section icon="shield" title="Lisans Ataması" subtitle="Bir lisans planı seçin; bitiş tarihi plan süresinden hesaplanır"
          right={<button className="dps-blk-add" onClick={() => navigate('/licenses')} type="button"><Icon name="plus" size={15} /> Lisans Planları</button>}>
          <Grid cols={2}>
            <FieldSelect form={form} set={set} k="licenseId" label="Lisans Planı" options={licenseOptions} />
            <FieldDate form={form} set={set} k="licenseStartDate" label="Başlangıç Tarihi" />
          </Grid>
          {selectedLicense && (
            <FieldCustom label="Seçilen Plan">
              <div style={{ fontSize: 13, color: 'var(--text-secondary)', lineHeight: 1.7 }}>
                <b style={{ color: 'var(--text-primary)' }}>{pick(selectedLicense, 'edition', 'Edition')}</b>
                {' · '}{pick(selectedLicense, 'maxUsers', 'MaxUsers')} kullanıcı
                {' · '}{pick(selectedLicense, 'durationDays', 'DurationDays') ? `${pick(selectedLicense, 'durationDays', 'DurationDays')} gün` : 'süresiz'}
                {previewEnd && <> · Bitiş: <b style={{ color: 'var(--text-primary)' }}>{fmtDate(previewEnd)}</b></>}
              </div>
            </FieldCustom>
          )}
          {!isNew && currentSnapshot && (
            <FieldCustom label="Mevcut Atanmış Lisans (snapshot)">
              <div style={{ fontSize: 13, color: 'var(--text-secondary)', lineHeight: 1.7 }}>
                {currentSnapshot.Name ? <>{currentSnapshot.Name} · </> : null}
                {currentSnapshot.Edition} · {currentSnapshot.MaxUsers} kullanıcı
                {currentSnapshot.ExpiresAt ? <> · Bitiş: {fmtDate(currentSnapshot.ExpiresAt)}</> : ' · süresiz'}
              </div>
            </FieldCustom>
          )}
        </Section>

        <Section icon="shield" title="Şifre Politikası" subtitle="Bu tenant'taki tüm yeni kullanıcılar ve şifre değişiklikleri seçilen seviyeye uymak zorundadır">
          <Grid cols={2}>
            <FieldSelect form={form} set={set} k="passwordPolicyLevel" label="Şifre Zorluk Seviyesi" options={PASSWORD_LEVELS} />
          </Grid>
        </Section>

        <Section icon="mail" title="Mail Sunucusu (SMTP)" subtitle="Geçici şifre ve şifre değişikliği mailleri bu sunucudan gönderilir"
          note="Boş bırakılırsa sistemin genel (global) mail ayarları kullanılır. İleride bu tenant'a ait tüm mail gönderimleri de bu sunucudan yapılır.">
          <Grid cols={2}>
            <FieldText form={form} set={set} k="smtpHost" label="Sunucu (Host)" placeholder="orn: smtp.firma.com" />
            <FieldText form={form} set={set} k="smtpPort" label="Port" num placeholder="587" />
            <FieldSelect form={form} set={set} k="smtpEnableSsl" label="SSL/TLS" options={SSL_OPTIONS} />
            <FieldText form={form} set={set} k="smtpFromAddress" label="Gönderen Adresi (From)" type="email" placeholder="noreply@firma.com" />
            <FieldText form={form} set={set} k="smtpFromName" label="Gönderen Adı" placeholder="Firma Adı" />
            <FieldText form={form} set={set} k="smtpUsername" label="Kullanıcı Adı" />
            <FieldText form={form} set={set} k="smtpPassword" label="Şifre" type="password" placeholder="(değiştirmek için doldurun)" />
          </Grid>
        </Section>

        {isNew && (
          <Section icon="user" title="İlk Yönetici (Admin)" subtitle="Tenant kaydında otomatik oluşturulur"
            note="Tenant kaydedildiğinde varsayılan roller (Yönetici/Mobil), tüm menü yetkileri ve bu admin kullanıcı otomatik oluşturulur. Admin şifresi sistem tarafından şifre politikasına uygun otomatik üretilir ve tenant mail sunucusundan gönderilir; admin ilk girişte şifresini değiştirir.">
            <Grid cols={2}>
              <FieldText form={form} set={set} tried={tried} k="adminEmail" label="E-posta" req type="email" placeholder="admin@firma.com" />
              <FieldText form={form} set={set} k="adminFirstName" label="Ad" />
              <FieldText form={form} set={set} k="adminLastName" label="Soyad" />
            </Grid>
          </Section>
        )}
      </div>
      )}

      {tab === 'users' && (
      <div className="dps-form">
        <Section icon="users" title="Kullanıcılar"
          subtitle="Bu tenant'a bağlı kullanıcılar — tür bazında filtre ve aktif sayı özeti (salt-görüntüleme).">
          {/* Tür bazında AKTİF kullanıcı sayısı özeti — örn. Mobil = 95 / Admin = 3 */}
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginBottom: 14, alignItems: 'center' }}>
            <span style={{ fontSize: 12, fontWeight: 700, color: 'var(--text-secondary)', marginRight: 2 }}>
              Aktif kullanıcılar:
            </span>
            {USER_TYPES.map((t) => (
              <span key={t.key} style={{
                display: 'inline-flex', alignItems: 'center', gap: 6, padding: '3px 10px',
                borderRadius: 999, fontSize: 13, fontWeight: 600,
                background: 'var(--surface-2, rgba(15,23,42,0.05))', color: 'var(--text-primary)',
              }}>
                <StatusBadge tone={t.tone}>{t.label}</StatusBadge>
                <b style={{ fontVariantNumeric: 'tabular-nums' }}>{typeCounts[t.key]?.active ?? 0}</b>
              </span>
            ))}
            <span style={{
              display: 'inline-flex', alignItems: 'center', gap: 6, padding: '3px 10px',
              borderRadius: 999, fontSize: 13, fontWeight: 700,
              background: 'var(--surface-2, rgba(15,23,42,0.05))', color: 'var(--text-secondary)',
            }}>
              Toplam Aktif: <b style={{ color: 'var(--text-primary)', fontVariantNumeric: 'tabular-nums' }}>{typeCounts.ALL?.active ?? 0}</b>
            </span>
          </div>

          {/* Tür filtresi — her düğme o türdeki TOPLAM (aktif+pasif) kayıt sayısını gösterir */}
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 12 }}>
            {[{ key: 'ALL', label: 'Tümü' }, ...USER_TYPES].map((t) => {
              const on = typeFilter === t.key;
              return (
                <button key={t.key} type="button" onClick={() => setTypeFilter(t.key)}
                  style={{
                    padding: '5px 12px', borderRadius: 8, fontSize: 13, fontWeight: 600, cursor: 'pointer',
                    border: '1px solid ' + (on ? 'var(--primary, #2563eb)' : 'var(--border-color, rgba(15,23,42,0.14))'),
                    background: on ? 'var(--primary, #2563eb)' : 'transparent',
                    color: on ? '#fff' : 'var(--text-primary)',
                  }}>
                  {t.label} <span style={{ opacity: 0.8 }}>({typeCounts[t.key]?.total ?? 0})</span>
                </button>
              );
            })}
          </div>

          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 720 }}>
              <thead>
                <tr>
                  {['Ad Soyad', 'E-posta', 'Telefon', 'Tür', 'Durum'].map((h, i) => (
                    <th key={h} style={{
                      textAlign: i >= 3 ? 'left' : 'left', padding: '10px 12px', fontSize: 12, fontWeight: 700,
                      color: 'var(--text-secondary)', borderBottom: '1px solid var(--border-color, rgba(15,23,42,0.10))', whiteSpace: 'nowrap',
                    }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {usersLoading ? (
                  <tr><td colSpan={5} style={{ padding: '14px 12px', fontSize: 14, color: 'var(--text-secondary)' }}>Yükleniyor…</td></tr>
                ) : filteredUsers.length === 0 ? (
                  <tr><td colSpan={5} style={{ padding: '14px 12px', fontSize: 14, color: 'var(--text-secondary)' }}>Kayıt bulunamadı</td></tr>
                ) : filteredUsers.map((u) => {
                  const typeKey = pick(u, 'userType', 'UserType') || 'BACKOFFICE';
                  const meta = USER_TYPE_BY_KEY[typeKey] || { label: typeKey, tone: 'neutral' };
                  const active = !!pick(u, 'isActive', 'IsActive');
                  const tdc = { padding: '10px 12px', fontSize: 14, color: 'var(--text-primary)', borderBottom: '1px solid var(--border-color, rgba(15,23,42,0.06))' };
                  return (
                    <tr key={pick(u, 'id', 'Id')}>
                      <td style={tdc}>{pick(u, 'fullName', 'FullName') || '-'}</td>
                      <td style={{ ...tdc, color: 'var(--text-secondary)' }}>{pick(u, 'email', 'Email') || '-'}</td>
                      <td style={{ ...tdc, color: 'var(--text-secondary)', whiteSpace: 'nowrap' }}>{pick(u, 'phoneNumber', 'PhoneNumber') || '-'}</td>
                      <td style={tdc}><StatusBadge tone={meta.tone}>{meta.label}</StatusBadge></td>
                      <td style={tdc}><StatusBadge tone={active ? 'ok' : 'neutral'}>{active ? 'Aktif' : 'Pasif'}</StatusBadge></td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </Section>
      </div>
      )}

      {deleteConfirm && (
        <SubModal
          title="Silme Onayı" maxWidth={440} onClose={() => setDeleteConfirm(false)}
          footer={(
            <>
              <button className="dps-btn ghost" onClick={() => setDeleteConfirm(false)} disabled={saving}>İptal</button>
              <button className="dps-btn danger" onClick={handleDelete} disabled={saving}>{saving ? 'Siliniyor…' : 'Evet, Sil'}</button>
            </>
          )}
        >
          <p style={{ margin: 0, lineHeight: 1.6 }}>
            <strong>“{form.name || form.identifier}”</strong> tenant'ını silmek istediğinize emin misiniz?
            Bu işlem tenant'ın kayıt verilerini etkilemez, yalnız tenant tanımını kaldırır.
          </p>
        </SubModal>
      )}

      <Toast toast={toast} />
    </DetailShell>
  );
}

export default TenantDetailPage;
